import React from 'react';

import { useIntl } from 'react-intl-next';

import CalendarPlusIcon from '@atlaskit/icon/core/calendar-plus';
import FlagIcon from '@atlaskit/icon/core/flag';
import PremiumIcon from '@atlaskit/icon/core/premium';
import Image from '@atlaskit/image';
import { ConfluenceLogo } from '@atlaskit/logo';
import { Modal } from '@atlassian/growth-pattern-library-modal';

import ConfluenceStandardOfferCalendarModalRightSideSVG from '../../assets/svg/ConfluenceStandardOfferCalendarModalRightSide.svg';
import ConfluenceStandardOfferCalendarModalRightSideSVGDark from '../../assets/svg/ConfluenceStandardOfferCalendarModalRightSideDark.svg';
import { ScreenEvent, useAnalytics } from '../../utils/analytics';
import {
	type BusinessLogicAnalyticsAttributes,
	type CommonAnalyticsAttributes,
	CONFLUENCE_STANDARD_OFFER_CALENDAR_MODAL,
	type ModalDismissalMethod,
	type ModalShownPreOrPostUpgrade,
	type OfferShownType,
	type SupportedProduct,
} from '../../utils/types';
import { useModalState } from '../../utils/useModalState';
import { EditionHighlightsLeftContent } from '../EditionHighlightsLeftContent';
import { PrimaryAndSecondaryCTAActionsGroup } from '../PrimaryAndSecondaryCTAActionsGroup';
import { SideContentWithModalCloseButton } from '../SideContentWithModalCloseButton';

import { messages } from './messages';

export const StandardOfferCalendarModalRightSide = () => (
	<Image
		src={ConfluenceStandardOfferCalendarModalRightSideSVG}
		srcDark={ConfluenceStandardOfferCalendarModalRightSideSVGDark}
		alt="Calendars"
	/>
);

export const ConfluenceStandardOfferCalendarModal = ({
	analyticsAttributes,
	experienceUrl,
}: {
	analyticsAttributes: BusinessLogicAnalyticsAttributes;
	experienceUrl: string;
}) => {
	const intl = useIntl();

	const { isOpen, handleClose } = useModalState();

	const componentAttributes: CommonAnalyticsAttributes & BusinessLogicAnalyticsAttributes = {
		...analyticsAttributes,
		product: 'confluence' as SupportedProduct,
		offerShown: 'CALENDAR' as OfferShownType,
		modalShownPrePostUpgrade: 'PRE_UPGRADE' as ModalShownPreOrPostUpgrade,
	};

	const { clickedDismissModal, clickedPrimaryCTAOnModal } = useAnalytics();

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			height="560px"
			logo={<ConfluenceLogo shouldUseNewLogoDesign size="xsmall" />}
			title={intl.formatMessage(messages.standardOfferCalendarTitle)}
			subtitle={intl.formatMessage(messages.standardOfferCalendarSubtitle)}
			testId="growth-pattern-library-modal"
			onClose={() => {
				clickedDismissModal(CONFLUENCE_STANDARD_OFFER_CALENDAR_MODAL, {
					...componentAttributes,
					dismissalMethod: 'modalOverlayClick' as ModalDismissalMethod,
				});
				handleClose();
			}}
			sideContent={SideContentWithModalCloseButton(
				<StandardOfferCalendarModalRightSide />,
				() => {
					clickedDismissModal(CONFLUENCE_STANDARD_OFFER_CALENDAR_MODAL, {
						...componentAttributes,
						dismissalMethod: 'closeButton' as ModalDismissalMethod,
					});
					handleClose();
				},
			)}
			actions={
				<PrimaryAndSecondaryCTAActionsGroup
					secondaryCTALabel={intl.formatMessage(messages.standardOfferCalendarSecondaryCTA)}
					onSecondaryCTAClick={() => {
						clickedDismissModal(CONFLUENCE_STANDARD_OFFER_CALENDAR_MODAL, {
							...componentAttributes,
							dismissalMethod: 'secondaryCTAButton' as ModalDismissalMethod,
						});
						handleClose();
					}}
					primaryCTALabel={intl.formatMessage(messages.standardOfferCalendarPrimaryCTA)}
					onPrimaryCTAClick={() => {
						clickedPrimaryCTAOnModal(CONFLUENCE_STANDARD_OFFER_CALENDAR_MODAL, {
							...componentAttributes,
							changeOfferingUrl: '', // TODO to add in LUNA-615
						});
					}}
					href={experienceUrl}
					isLoading={false}
				/>
			}
		>
			<EditionHighlightsLeftContent
				highlights={[
					{
						icon: CalendarPlusIcon,
						title: intl.formatMessage(messages.calendarBenefitsTrack),
					},
					{
						icon: FlagIcon,
						title: intl.formatMessage(messages.calendarBenefitsSeeDeadlines),
					},
					{
						icon: PremiumIcon,
						title: intl.formatMessage(messages.calendarBenefitsLinkEvents),
					},
				]}
				disclaimerText={intl.formatMessage(messages.standardOfferCalendarDisclaimerText)}
			/>
			<ScreenEvent name="upgradeFreePoolTargetExperimentModal" attributes={componentAttributes} />
		</Modal>
	);
};
